import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarAlt, FaArrowLeft } from 'react-icons/fa';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import useNews from '../hooks/useNews';
import { formatDate, getYoutubeVideoId } from '../utils/stringUtils';
import { useDeviceDetection, getOptimizedBackgroundStyles } from '../utils/deviceUtils';

const NewsDetail = () => {
  const { slug } = useParams();
  const { getNewsBySlug, loading, error } = useNews();
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetection();
  
  const newsItem = getNewsBySlug(slug);
  
  useEffect(() => {
    if (!loading && !error && !newsItem) {
      navigate('/news');
    }
  }, [newsItem, loading, error, navigate]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="neon-loader">
          <div className="circle"></div>
          <div className="circle"></div>
        </div>
      </div>
    );
  }
  
  if (error || !newsItem) {
    return (
      <div className="min-h-screen bg-dark flex flex-col items-center justify-center text-white">
        <h2 className="text-2xl font-bold mb-4">Không tìm thấy bài viết</h2>
        <button 
          onClick={() => navigate('/news')}
          className="flex items-center px-4 py-2 bg-accent-teal/20 text-accent-teal rounded hover:bg-accent-teal/30 transition-all"
        >
          <FaArrowLeft className="mr-2" />
          Quay lại danh sách tin tức
        </button>
      </div>
    );
  }
  
  const youtubeId = getYoutubeVideoId(newsItem.youtube);
  
  // Background styles with optimization
  const backgroundStyles = getOptimizedBackgroundStyles({
    backgroundImage: `url(${newsItem?.image})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundAttachment: isMobile ? 'scroll' : 'fixed'
  }, isMobile);
  
  return (
    <>
      <SEO 
        title={`${newsItem?.title}`}
        url={`https://psa.world/news/${newsItem?.slug}`}
        description={newsItem?.summary}
        image={newsItem?.image}
      />

      <Helmet>
        <meta name="description" content={newsItem.summary} />
        <meta property="og:title" content={`${newsItem.title}`} />
        <meta property="og:description" content={newsItem.summary} />
        <meta property="og:image" content={newsItem.image} />
        <meta property="og:type" content={youtubeId ? "video.other" : "article"} />
        <meta property="og:url" content={window.location.href} />
        {youtubeId && (
          <>
            <meta property="og:video" content={`https://www.youtube.com/watch?v=${youtubeId}`} />
            <meta property="og:video:url" content={`https://www.youtube.com/watch?v=${youtubeId}`} />
            <meta property="og:video:secure_url" content={`https://www.youtube.com/watch?v=${youtubeId}`} />
            <meta property="og:video:type" content="text/html" />
            <meta property="og:video:width" content="1280" />
            <meta property="og:video:height" content="720" />
            <meta property="video:duration" content="120" />
          </>
        )}
        <meta name="twitter:card" content={youtubeId ? "player" : "summary_large_image"} />
        <meta name="twitter:title" content={`${newsItem.title}`} />
        <meta name="twitter:description" content={newsItem.summary} />
        <meta name="twitter:image" content={newsItem.image} />
        {youtubeId && (
          <>
            <meta name="twitter:player" content={`https://www.youtube.com/embed/${youtubeId}`} />
            <meta name="twitter:player:width" content="1280" />
            <meta name="twitter:player:height" content="720" />
          </>
        )}
        
        {/* Structured data for SEO */}
        {youtubeId ? (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              "name": newsItem.title,
              "description": newsItem.summary,
              "thumbnailUrl": `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
              "uploadDate": newsItem.date,
              "contentUrl": `https://www.youtube.com/watch?v=${youtubeId}`,
              "embedUrl": `https://www.youtube.com/embed/${youtubeId}`,
              "duration": "PT2M",
              "interactionStatistic": {
                "@type": "InteractionCounter",
                "interactionType": { "@type": "WatchAction" },
                "userInteractionCount": 5000
              },
              "publisher": {
                "@type": "Organization",
                "name": "PSA Roleplay",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://psa.world/resources/images/logo_new.png"
                }
              },
              "hasPart": {
                "@type": "Clip",
                "name": newsItem.title,
                "startOffset": 0,
                "endOffset": 120,
                "url": `https://www.youtube.com/watch?v=${youtubeId}`
              }
            })}
          </script>
        ) : (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              "headline": newsItem.title,
              "image": [newsItem.image],
              "author": {
                "@type": "Person",
                "name": "PSA Team"
              },
              "publisher": {
                "@type": "Organization",
                "name": "PSA Roleplay",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://psa.world/resources/images/logo_new.png"
                }
              },
              "datePublished": newsItem.date,
              "dateModified": newsItem.date,
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": window.location.href
              },
              "description": newsItem.summary
            })}
          </script>
        )}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Trang chủ",
                "item": "https://psa.world"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Tin tức tại PSA Roleplay",
                "item": "https://psa.world/news"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": newsItem.title,
                "item": `https://psa.world/news/${newsItem.slug}`
              }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Wrapper container */}
      <div className="min-h-screen bg-dark-purple-main">
        {/* Entire page section with thumbnail background */}
        <section className="relative pt-32 pb-16 bg-gradient-to-b from-dark-purple to-dark min-h-screen">
          {/* Background image with gradient overlay */}
          <div className="absolute inset-0 z-0">
            <div 
              className="absolute inset-0 bg-center opacity-50"
              style={backgroundStyles}
            ></div>
            {/* Gradient overlay chỉ phủ từ dưới lên */}
            <div className="absolute inset-x-0 bottom-0 h-2/3" 
                 style={{
                   background: 'linear-gradient(to top, #121212 0%,rgba(18, 18, 18, 0.8) 30%,rgba(18, 18, 18, 0.4) 60%,rgba(18, 18, 18, 0) 100%)'
                 }}>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Back button */}
            <motion.button
              onClick={() => navigate('/news')}
              className="flex items-center px-4 py-2 mb-6 bg-dark/40 backdrop-blur-sm text-light rounded-lg hover:bg-dark/60 transition-all"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <FaArrowLeft className="mr-2" />
              Quay lại tin tức
            </motion.button>
            
            {/* Title */}
            <motion.h1
              className="text-3xl md:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {newsItem.title}
            </motion.h1>
            
            {/* Date */}
            <motion.div
              className="flex items-center mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <FaCalendarAlt className="text-accent-teal mr-2" />
              <span className="text-light/80">{formatDate(newsItem.date)}</span>
            </motion.div>
            
            {/* Separator line */}
            <motion.div
              className="w-24 h-1 bg-accent-teal mb-8"
              initial={{ width: 0 }}
              animate={{ width: 96 }}
              transition={{ delay: 0.5 }}
            />

            {/* YouTube Video - Đã được di chuyển lên trước nội dung chính */}
            {youtubeId && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-8 aspect-video w-full rounded-lg overflow-hidden shadow-xl"
              >
                <iframe 
                  width="100%" 
                  height="100%" 
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  title={newsItem.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </motion.div>
            )}
            
            {/* Content section */}
            <AnimatePresence mode="wait">
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-br from-dark/80 to-dark/40 backdrop-blur-md border border-white/10 rounded-xl p-8 shadow-xl mb-16"
              >
                {/* Summary */}
                <div className="mb-8 text-xl text-light/90 font-medium border-l-4 border-accent-teal pl-4">
                  {newsItem.summary}
                </div>
                
                {/* Article Content */}
                <div 
                  className="prose prose-invert prose-accent-teal max-w-none text-light/90"
                  dangerouslySetInnerHTML={{ __html: newsItem.content }}
                ></div>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>
      </div>
    </>
  );
};

export default NewsDetail;
